import { i18n } from 'app/utils/i18n';
import { getConceptTypeContent, getTagLabel } from 'app/utils/tag-utils';
import { TagItemShortcuts } from 'app/views/documents/tagging/TagItemShortcuts/TagItemShortcuts';
import { Typography } from 'app/components/ui';
import { TagWithMetadata } from 'app/api/tag-queries';
import { useGoToTag } from 'app/utils/hooks/useGoToTag';
import { formatTaxonomyName } from 'app/utils/tag-utils';

import './OverviewTable.scss';

interface OverviewTableProps {
  categories: string[];
  tableData: TagWithMetadata[];
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onNavigate?: () => void;
  pageSize?: number;
}

export const OverviewTable = ({ categories, tableData, onTagDetailsClick, onNavigate, pageSize }: OverviewTableProps) => {
  const { goToTag, getGoToTagState } = useGoToTag();

  const handleGoToTag = (tag: TagWithMetadata) => {
    if (getGoToTagState(tag).disabled) return;
    onNavigate?.();
    goToTag(tag);
  };

  return (
    <table className="overview-table">
      <thead>
        <tr>
          {categories.map((category) => (
            <th key={category}>
              <Typography variant="body-7" fontWeight="semibold" color="#547ba2">
                {category}
              </Typography>
            </th>
          ))}
          <th></th>
        </tr>
      </thead>

      <tbody>
        {tableData.length > 0 ? (
          <>
            {tableData.map((tag) => {
              const label = tag.concept?.labels ? getTagLabel({ languages: tag.concept.labels, currentLang: 'sv' }) : tag.concept?.name || '-';
              const { conceptLabel } = tag.concept?.conceptType ? getConceptTypeContent(tag.concept.conceptType) : { conceptLabel: '-' };
              const goToState = getGoToTagState(tag);
              return (
                <tr
                  key={tag.id}
                  className={`overview-table__row${goToState.disabled ? ' overview-table__row--disabled' : ''}`}
                  onClick={() => handleGoToTag(tag)}
                >
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {label}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {conceptLabel}
                    </Typography>
                  </td>
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {tag.taxonomy?.name ? formatTaxonomyName(tag.taxonomy.name) : '-'}
                    </Typography>
                  </td>
                  <td>
                    <div className={`overview-table__tag-status overview-table__tag-status--${tag.status}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {tag.status ? i18n(`tag.status.${tag.status}`) : '-'}
                      </Typography>
                    </div>
                  </td>
                  <td>
                    <div className={`overview-table__tag-status overview-table__tag-status--${tag.validationStatus}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {tag.validationStatus ? i18n(`tag.status.${tag.validationStatus}`) : '-'}
                      </Typography>
                    </div>
                  </td>
                  <td className="overview-table__icon-btn">{tag.concept?.isMandatory ? <i className="fa-solid fa-check" /> : null}</td>
                  <td>
                    <div className={`overview-table__origin overview-table__origin--${tag.origin ?? 'human'}`}>
                      <Typography variant="body-3" fontWeight="semibold">
                        {i18n(`tag.ai.origin.${tag.origin ?? 'human'}`)}
                      </Typography>
                    </div>
                  </td>
                  <td>
                    <Typography variant="body-7" fontWeight="light">
                      {tag.metadata?.pageNumber ?? '-'}
                    </Typography>
                  </td>
                  <td>
                    <TagItemShortcuts
                      actions={[
                        ...(onTagDetailsClick ? [{ type: 'details' as const, onClick: () => onTagDetailsClick(tag) }] : []),
                        {
                          type: 'navigate' as const,
                          disabled: goToState.disabled,
                          tooltip: goToState.tooltip,
                          onClick: () => handleGoToTag(tag),
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
            {pageSize &&
              tableData.length < pageSize &&
              Array.from({ length: pageSize - tableData.length }, (_, i) => (
                <tr key={`empty-${i}`} className="overview-table__empty-row">
                  <td colSpan={categories.length + 1}>&nbsp;</td>
                </tr>
              ))}
          </>
        ) : (
          <tr>
            <td colSpan={categories.length + 1}>{i18n('all.tags.empty.list')}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
